/*
 * HappyLife: kleurstalen onder een productkaart in de collectie.
 * Hoort bij snippets/card-color-swatches.liquid.
 *
 * Klik op een bolletje: de foto van de kaart wisselt naar de variantfoto en de
 * link van de kaart krijgt ?variant=... mee, zodat de klant op de productpagina
 * meteen de juiste kleur ziet staan.
 *
 * De collectiepagina bouwt zichzelf opnieuw op bij filteren en sorteren. Dit is
 * een custom element, dus connectedCallback draait dan vanzelf opnieuw en er
 * blijven geen losse listeners achter.
 */
if (!customElements.get('card-swatches')) {
  class CardSwatches extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.card = this.closest('.card-wrapper');
      this.addEventListener('click', this.onClick.bind(this));
    }

    onClick(event) {
      const button = event.target.closest('.card-swatches__item');
      if (!button || !this.card) return;

      event.preventDefault();
      this.select(button);
    }

    select(button) {
      this.querySelectorAll('.card-swatches__item').forEach((item) => {
        const isChosen = item === button;
        item.classList.toggle('is-selected', isChosen);
        item.setAttribute('aria-pressed', isChosen ? 'true' : 'false');
      });

      this.card.classList.add('card--swatch-picked');
      this.updateLinks(button.dataset.variantId);
      this.updateQuickAdd(button.dataset.variantId);
      this.swapImage(button);
    }

    /*
     * Staat er een snel-toevoegknop op de kaart, dan moet die de gekozen kleur
     * toevoegen en niet de standaardvariant. Bij de knop die een venster opent
     * (quick-add-modal) staat dit veld er niet; dat venster kiest zelf.
     */
    updateQuickAdd(variantId) {
      if (!variantId) return;

      const input = this.card.querySelector('input.product-variant-id');
      if (input) input.value = variantId;
    }

    /*
     * Foto wisselen.
     *
     * We laden de nieuwe foto eerst in het geheugen en wisselen pas als hij
     * klaar is. Zetten we de src meteen, dan is de <img> even leeg terwijl het
     * bestand binnenkomt - dat is de lege ruimte die je zag, zeker als je er op
     * dat moment ook overheen zweefde.
     *
     * Beide foto's in de kaart krijgen dezelfde bron. De tweede is normaal de
     * hover-foto van de oorspronkelijke kleur; die kan door de CSS niet meer in
     * beeld komen, maar zo staat er hoe dan ook nooit iets leegs of iets in de
     * verkeerde kleur. Het is dezelfde URL, dus het kost geen extra download.
     *
     * De teller vangt snel achter elkaar klikken af: alleen de laatste klik mag
     * het beeld nog aanpassen.
     */
    swapImage(button) {
      const images = this.card.querySelectorAll('.card__media img');
      const source = button.dataset.src;
      if (images.length === 0 || !source) return;

      const token = (this.swapToken || 0) + 1;
      this.swapToken = token;

      const apply = () => {
        if (this.swapToken !== token) return;
        images.forEach((image) => {
          image.removeAttribute('loading');
          image.srcset = button.dataset.srcset || '';
          image.src = source;
          if (button.dataset.alt) image.alt = button.dataset.alt;
        });
      };

      const preloader = new Image();
      if (button.dataset.srcset) preloader.srcset = button.dataset.srcset;
      preloader.sizes = images[0].sizes || '';
      preloader.src = source;

      if (preloader.complete) {
        apply();
        return;
      }

      preloader.addEventListener('load', apply, { once: true });
      preloader.addEventListener('error', apply, { once: true });

      /* Blijft het laden hangen, dan wisselen we alsnog. Beter een korte
         overgang dan een bolletje dat niets lijkt te doen. */
      setTimeout(apply, 1200);
    }

    /* Alle links van deze kaart naar de gekozen variant laten wijzen. */
    updateLinks(variantId) {
      if (!variantId) return;

      const base = this.dataset.productUrl;
      if (!base) return;

      const url = `${base}${base.includes('?') ? '&' : '?'}variant=${variantId}`;
      this.card.querySelectorAll(`a[href^="${base}"]`).forEach((link) => {
        link.href = url;
      });
    }
  }

  customElements.define('card-swatches', CardSwatches);
}
